import React, { useEffect, useState, useMemo } from 'react'
import { Modal, Icon, Spin } from 'antd'
import { connect } from 'react-redux'
import moment from 'moment'
import { getAssetImages } from '../../appRedux/actions/Asset'
import PhotoLightbox from './PhotoLightbox'

/**
 * PhotoCompare
 *
 * Side-by-side comparison of THIS audit's photo against the asset's EARLIER
 * photos, so an auditor can tell whether an asset actually moved (or is even the
 * same physical unit) rather than just reading two location strings.
 *
 * Why not "scanned vs master register": once an audit report is submitted its
 * photo is merged into the asset, so those two are the same picture. The useful
 * comparison is against the asset's photo HISTORY.
 *
 * Data sources — deliberately different on each side:
 *  - LEFT  : `record.photos`, the photos captured against THIS AST record. They
 *            are already inline on the record, so this side needs no request and
 *            is always exactly the audit's own evidence.
 *  - RIGHT : GET /common/asset-image/{assetId} (redux `asset.assetImages`),
 *            fetched ONCE per asset and only when this modal opens — never per
 *            table row. Each image carries its own voucher, time, location and
 *            sub-location, which is what makes the comparison meaningful.
 *
 * Data reliability:
 *  - The current audit's own entry is excluded from the "earlier photos" strip by
 *    matching `voucherId` to the record's `astId`. If that id is missing we do
 *    NOT guess by date — the entry stays in the strip, labelled with its voucher,
 *    so nothing is silently hidden or mislabelled.
 *  - Missing data is stated, never filled in: no photos on the record, no master
 *    asset, and an empty history each render their own message.
 */

const fmtTime = (t) => (t ? moment(t).format('DD MMM YYYY, HH:mm') : '—')

// Two images are "in the same place" only when BOTH location and sub-location
// match. Blank is not treated as equal to blank-but-different — an unknown place
// is shown as unknown rather than assumed to match.
const samePlace = (a, b) =>
  !!a && !!b && (a.location || '') === (b.location || '') && (a.client || '') === (b.client || '')

const PhotoCompare = ({ visible, record, onClose, actions, ...props }) => {
  const [sel, setSel] = useState(0)
  const [auditIdx, setAuditIdx] = useState(0)
  // Which set the zoom viewer is showing, and where in it.
  const [zoom, setZoom] = useState(null) // { set: 'audit' | 'history', index }

  // Which asset's photo history is worth comparing against:
  //  - AST: the master asset the scan matched.
  //  - PV : only once it has been LINKED to one. An unlinked PV is a newly found
  //         item with no register counterpart, so there is nothing to compare -
  //         the second pane and the history strip are not rendered at all rather
  //         than showing an empty frame.
  const isPv = !!(record && record.source === 'PV')
  const isAsset = !!(record && record.source === 'ASSET')
  const linkedAssetId = (isPv && record.actionTaken && record.actionTaken.name === 'LINK_ASSET'
    && record.actionTaken.refRecordId) ? String(record.actionTaken.refRecordId) : null
  // An Assets-tab row IS the register asset, so its own history is the comparison.
  const assetId = isAsset
    ? (record._id && !String(record._id).startsWith('new-') ? String(record._id) : null)
    : isPv
      ? linkedAssetId
      : (record && record.matchedAsset && record.matchedAsset._id)
  const comparable = !!assetId
  const linkedCode = record && record.actionTaken && record.actionTaken.refRecordSubId
  const auditPhotos = useMemo(() => {
    if (!record) return []
    // An Assets row has no photos of its own; the audit evidence, if any, belongs
    // to the AST or PV record that accounted for it.
    const src = isAsset
      ? ((record.astRef && record.astRef.photos) || (record.pvRef && record.pvRef.photos) || [])
      : (record.photos || [])
    return Array.isArray(src) ? src.filter(p => p && p.url) : []
  }, [record, isAsset])

  // An Assets row that nothing accounted for - Missing, or Out of scope - was
  // never seen in this audit. There is no evidence pane to draw, so it is not
  // drawn: an empty frame reads as a missing photo rather than a missing sighting.
  const seenInAudit = !isAsset || !!(record && (record.astRef || record.pvRef))

  // Vouchers whose photos are THIS audit's, so the same picture is not shown on
  // both sides once it has been merged into the asset.
  const auditVoucherIds = useMemo(() => {
    const ids = new Set()
    const add = (v) => { if (v) ids.add(String(v)) }
    if (!record) return ids
    if (isAsset) {
      add(record.astRef && record.astRef.astId)
      add(record.pvRef && record.pvRef.pvId)
      add(record.pvRef && record.pvRef._id)
    } else if (isPv) {
      add(record.pvId); add(record._id)
    } else {
      add(record.astId)
    }
    return ids
  }, [record, isAsset, isPv])

  // The same photo by URL. Submitting an AST report, and processing a PV link,
  // copies the audit photo onto the asset - so the history legitimately contains
  // the picture already shown on the left.
  const auditUrls = useMemo(() => new Set(auditPhotos.map(p => p.url)), [auditPhotos])

  // Fetch the asset's photo history once, on open, and only if we don't have it.
  useEffect(() => {
    if (visible && assetId && !props.assetImages[assetId]) props.getAssetImages(assetId)
  }, [visible, assetId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setSel(0); setAuditIdx(0) }, [record && record._uid])

  // Earlier photos: everything on the asset except this audit's own entry,
  // newest first.
  const history = useMemo(() => {
    const all = (assetId && props.assetImages[assetId]) || []
    return all
      .filter(im => im && im.url)
      // Drop this audit's own photos, matched two ways: by the voucher they came
      // from, and by identical URL for the copies that submitting/processing makes.
      // Neither guesses - an entry with no voucherId and a URL we have not seen
      // stays in the strip, labelled, rather than being dropped on a hunch.
      .filter(im => !(im.voucherId && auditVoucherIds.has(String(im.voucherId))))
      .filter(im => !auditUrls.has(im.url))
      .slice()
      .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0))
  }, [assetId, props.assetImages, auditVoucherIds, auditUrls])

  // Once the audit's photos are merged onto the asset, dedup can legitimately
  // leave NO earlier photos - very common for a freshly processed record. Say so
  // rather than drawing an empty plate.
  const historyLoaded = !!(assetId && props.assetImages[assetId])
  const showHistory = comparable && (!historyLoaded || history.length > 0)

  const current = history[sel] || null
  const audit = auditPhotos[auditIdx] || null

  const scannedPlace = record
    ? { location: props.locName(record.location), client: props.clientName(record.client) }
    : null

  // Clicking a plate opens the zoom viewer on that pane's own set, so paging
  // inside the viewer stays within the audit's photos or the asset's history
  // rather than mixing the two.
  const plate = (src, alt, tone, onZoom) => (
    <div className={'pc-plate' + (tone ? ' ' + tone : '') + (src ? ' zoomable' : '')}
      onClick={src ? onZoom : undefined}
      title={src ? 'Click to zoom' : undefined}>
      {src
        ? <><img src={src} alt={alt} /><span className="pc-zoom"><Icon type="zoom-in" /></span></>
        : <div className="pc-none"><Icon type="picture" /><span>{alt}</span></div>}
    </div>
  )

  const placeLine = (loc, sub, differs) => (
    <div className="pc-place">
      <div>{loc || <em>location not recorded</em>}</div>
      <div className="sub">
        Sub-location <span className={differs ? 'd' : ''}>{sub || '—'}</span>
      </div>
    </div>
  )

  const locDiffers = !!(current && scannedPlace && (current.location || '') !== (scannedPlace.location || ''))
  const subDiffers = !!(current && scannedPlace && (current.client || '') !== (scannedPlace.client || ''))

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1080}
      centered
      destroyOnClose
      bodyStyle={{ padding: 0 }}
      className="rcx-photo-modal"
      title={record ? (
        <div className="pc-head">
          <span className="pc-code">{isPv ? record.tempAssetCode : (record.assetCode || 'New asset')}</span>
          <span className="pc-meta">
            {(isPv
              ? [record.assetModel && record.assetModel.name,
                 record.equipmentSubType && record.equipmentSubType.name,
                 record.assetType && record.assetType.name]
              : [record.matchedAsset && record.matchedAsset.assetModel && record.matchedAsset.assetModel.name,
                 record.matchedAsset && record.matchedAsset.equipmentSubType && record.matchedAsset.equipmentSubType.name,
                 record.matchedAsset && record.matchedAsset.ownerCode]
            ).filter(Boolean).join(' · ')}
          </span>
          {isPv && linkedCode && <span className="pc-linked">linked to {linkedCode}</span>}
        </div>
      ) : null}
    >
      <div className="rcx">
        <Spin spinning={!!props.loading && !history.length}>
          <div className={'pc-body' + ((showHistory && seenInAudit) ? '' : ' solo')}>
            {seenInAudit && (
            <div className="pc-pane">
              <div className="pc-lbl scan">{isAsset ? 'Evidence from this audit' : 'Found in audit'}</div>
              <div className="pc-sub">
                {(() => {
                  const ref = isAsset ? (record.astRef || record.pvRef) : record
                  if (!ref) return <span className="pc-hint">not seen in this audit</span>
                  const no = ref.astNo || ref.pvNo || ref.tempAssetCode
                  const when = ref.time || ref.date
                  return <>{no ? <b>{no}</b> : null}{when ? <> · {fmtTime(when)}</> : null}</>
                })()}
              </div>
              {scannedPlace && placeLine(scannedPlace.location, scannedPlace.client, subDiffers)}
              {plate(audit && audit.url, auditPhotos.length ? 'audit photo' : 'no photo captured in this audit', 'scan',
                () => setZoom({ set: 'audit', index: auditIdx }))}
              {auditPhotos.length > 1 && (
                <div className="pc-strip small">
                  {auditPhotos.map((p, i) => (
                    <button key={i} className={'pc-thumb' + (i === auditIdx ? ' on' : '')}
                      onClick={() => setAuditIdx(i)}>
                      <span className="tb"><img src={p.url} alt="" /></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Never seen in this audit: say that, instead of an empty frame. */}
            {!seenInAudit && (
              <div className="pc-note">
                <Icon type="info-circle" />
                <span>
                  This asset was <b>not seen in this audit</b>
                  {record.assetStatus === 'oos'
                    ? ' — its location was outside the audit scope, so nobody looked for it.'
                    : ' — no scan matched it and no PV item was linked to it.'}
                  {' '}Its own photo history is shown{history.length ? '' : ', but there is none on record'}.
                </span>
              </div>
            )}

            {showHistory && (
            <div className="pc-pane">
              <div className="pc-lbl">
                Earlier photo
                {history.length > 1 && <span className="pc-hint">pick any below</span>}
              </div>
              <div className="pc-sub">
                {current ? (<>
                  {current.voucher ? <b>{current.voucher}</b> : <b>—</b>} · {fmtTime(current.time)}
                </>) : <span className="pc-hint">—</span>}
              </div>
              {current
                ? placeLine(
                    <span className={locDiffers ? 'd' : ''}>{current.location}</span>,
                    current.client, subDiffers)
                : <div className="pc-place"><div className="pc-hint">
                    No earlier photos on this asset.
                  </div></div>}
              {plate(current && current.url, 'no earlier photo', null,
                () => setZoom({ set: 'history', index: sel }))}
            </div>
            )}

            {/* Every photo on the asset came from this audit, so there is nothing
                earlier to compare against. */}
            {comparable && historyLoaded && history.length === 0 && seenInAudit && (
              <div className="pc-note">
                <Icon type="info-circle" />
                <span>
                  <b>No earlier photos.</b> Every photo on this asset was taken during this audit and is
                  shown on the left — submitting an AST report, and processing a PV link, copies the
                  audit photo onto the asset, so it is not repeated here.
                </span>
              </div>
            )}

            {/* An unlinked PV has no register counterpart at all, so say that in
                place of the second pane rather than rendering an empty frame. */}
            {!comparable && isAsset && (
              <div className="pc-note">
                <Icon type="info-circle" />
                <span>This asset does not exist in the register yet — it will be created when the
                  linked PV record is processed, and its photo history starts then.</span>
              </div>
            )}
            {!comparable && isPv && (
              <div className="pc-note">
                <Icon type="info-circle" />
                <span>
                  This item was found with no asset code, so there is nothing in the register to
                  compare it against yet. Link it to an asset and its photo history appears here.
                </span>
              </div>
            )}
            {!comparable && !isPv && !isAsset && (
              <div className="pc-note">
                <Icon type="info-circle" />
                <span>This code is not in the master register, so it has no photo history.</span>
              </div>
            )}
          </div>

          {history.length > 0 && (
            <div className="pc-histwrap">
              <div className="pc-histhead">
                Photo history · {history.length}
                <span className="pc-hint">amber marks a photo taken somewhere other than the one before it</span>
              </div>
              <div className="pc-strip">
                {history.map((im, i) => {
                  const older = history[i + 1]
                  const moved = older ? !samePlace(im, older) : false
                  return (
                    <button key={i} className={'pc-thumb' + (i === sel ? ' on' : '')}
                      title={`${im.voucher || ''} · ${fmtTime(im.time)}\n${im.location || ''} / ${im.client || ''}`}
                      onClick={() => setSel(i)}>
                      <span className="tb"><img src={im.url} alt="" /></span>
                      <span className="tc">
                        <b>{im.voucher || '—'}</b>
                        <span className={'movedot ' + (older ? (moved ? 'moved' : 'same') : 'first')} />
                        {im.time ? moment(im.time).format('DD MMM YYYY') : '—'}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {actions && <div className="pc-foot">{actions}</div>}
        </Spin>

        <PhotoLightbox
          visible={!!zoom}
          images={zoom && zoom.set === 'audit'
            ? auditPhotos.map((p, n) => ({
                url: p.url,
                caption: `${isPv ? record.tempAssetCode : record.assetCode} — found in audit`,
                sub: [isPv ? record.pvNo : record.astNo,
                      fmtTime(isPv ? record.date : record.time)].filter(Boolean).join(' · ')
                     + (auditPhotos.length > 1 ? ` · photo ${n + 1}` : '')
              }))
            : history.map(im => ({
                url: im.url,
                caption: `${linkedCode || (record.matchedAsset && record.matchedAsset.assetCode) || 'Register'} — earlier photo`,
                sub: [im.voucher, fmtTime(im.time),
                      [im.location, im.client].filter(Boolean).join(' / ')].filter(Boolean).join(' · ')
              }))}
          index={zoom ? zoom.index : 0}
          onIndex={(n) => {
            setZoom(z => ({ ...z, index: n }))
            // keep the pane behind the viewer on the same photo
            if (zoom && zoom.set === 'audit') setAuditIdx(n); else setSel(n)
          }}
          onClose={() => setZoom(null)}
        />
      </div>
    </Modal>
  )
}

const mapStateToProps = ({ asset, commonData }) => ({
  assetImages: asset.assetImages,
  loading: commonData.loading
})

export default connect(mapStateToProps, { getAssetImages })(PhotoCompare)
